import React from "react";
import {
  FieldWrapper,
  FilterMultiSelect,
  FilterSortGroup,
  formatGroupedValues,
} from "./jobsFilterUtils";
import AccountingWeekPicker from "../fields/AccountingWeekPicker";
import { formatWeekChipLabel, getStaffDisplayName } from "./accountingFilterUtils";

export const WEEKS_SORT_OPTIONS = [
  { value: "name", label: "Name" },
];

export function AccountingWeeksFilterFields({
  focusedFieldId,
  onFocusClear,
  innerTab,
  filterClientIds = [],
  setFilterClientIds,
  filterStaffIds = [],
  setFilterStaffIds,
  selectedStaffLookup = {},
  setSelectedStaffLookup,
  filterWeek,
  setFilterWeek,
  weekData,
  sortKey,
  setSortKey,
  sortDir,
  setSortDir,
  weekFilterClients = [],
  weekFilterStaff = [],
}) {
  const clientOptions = weekFilterClients.map((c) => ({
    value: String(c.id),
    label: String(c.name),
  }));

  const staffOptions = weekFilterStaff.map((s) => ({
    value: String(s.id),
    label: getStaffDisplayName(s) || s.name || `#${s.id}`,
  }));

  return (
    <>
      <FieldWrapper
        fieldId="client"
        label="Client"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterMultiSelect
          id="accounting-weeks-client"
          options={clientOptions}
          value={filterClientIds.map(String)}
          onChange={(vals) => setFilterClientIds(vals.map(Number))}
          placeholder="Select Client..."
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="staff"
        label="Staff"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterMultiSelect
          id="accounting-weeks-staff"
          options={staffOptions}
          value={filterStaffIds.map(String)}
          onChange={(vals) => {
            const numericVals = vals.map(Number);
            setFilterStaffIds(numericVals);
            // Maintain selectedStaffLookup cache
            setSelectedStaffLookup((prev) => {
              const next = { ...prev };
              // Remove deselected
              Object.keys(next).forEach((k) => {
                if (!numericVals.includes(Number(k))) {
                  delete next[k];
                }
              });
              // Add newly selected
              numericVals.forEach((id) => {
                if (!next[id]) {
                  const match = weekFilterStaff.find((s) => Number(s.id) === id);
                  if (match) {
                    next[id] = getStaffDisplayName(match) || String(id);
                  }
                }
              });
              return next;
            });
          }}
          placeholder="Select Staff..."
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="week"
        label="Week"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <AccountingWeekPicker
          selectedWeek={filterWeek}
          onWeekChange={setFilterWeek}
          weekData={weekData}
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="sort"
        label=""
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterSortGroup
          id="accounting-weeks-sort"
          sortBy={sortKey}
          sortOrder={sortDir}
          onSortByChange={setSortKey}
          onSortOrderChange={setSortDir}
          sortOptions={WEEKS_SORT_OPTIONS}
        />
      </FieldWrapper>
    </>
  );
}

export function buildAccountingWeeksFilterChips({
  searchTerm,
  innerTab,
  filterClientIds = [],
  filterStaffIds = [],
  filterWeek,
  weekData,
  weekFilterClients = [],
  weekFilterStaff = [],
  selectedStaffLookup = {},
}) {
  const chips = [];

  if (searchTerm) {
    chips.push({
      id: "search",
      key: "search",
      fieldId: "search",
      label: `Search: ${searchTerm}`,
    });
  }

  if (filterClientIds.length > 0) {
    const formatClient = (id) => {
      const match = weekFilterClients.find((c) => Number(c.id) === Number(id));
      return match?.name || `#${id}`;
    };
    chips.push({
      id: "client",
      key: "client",
      fieldId: "client",
      label: `Clients: ${formatGroupedValues(filterClientIds, formatClient, 3)}`,
    });
  }

  if (filterStaffIds.length > 0) {
    const formatStaff = (id) => {
      if (selectedStaffLookup[id]) return selectedStaffLookup[id];
      const match = weekFilterStaff.find((s) => Number(s.id) === Number(id));
      return getStaffDisplayName(match) || `#${id}`;
    };
    chips.push({
      id: "staff",
      key: "staff",
      fieldId: "staff",
      label: `Staff: ${formatGroupedValues(filterStaffIds, formatStaff, 3)}`,
    });
  }

  if (filterWeek) {
    chips.push({
      id: "week",
      key: "week",
      fieldId: "week",
      label: formatWeekChipLabel(filterWeek, weekData?.end_date),
    });
  }

  return chips;
}
